import { useCallback, useContext, useMemo, useSyncExternalStore } from 'react';

import { BannerContext } from './BannerContext.tsx';
import { getBanners, hideBanner, showBanner, subscribeToBanners } from './bannerStore.ts';
import { resolveTopNavHeight } from './resolveTopNavHeight.ts';

/**
 * Selector-based replacements for `<Subscribe to={[BannerStateContainer]}>`.
 *
 * Every hook returns a **primitive**, which is what keeps `getSnapshot` stable — `useSyncExternalStore`
 * compares with `Object.is`, so returning a freshly built object or array would loop forever. A
 * consumer therefore re-renders only when the value it actually reads changes, unlike unstated's
 * `Subscribe`, which re-renders every subscriber on every banner change.
 *
 * The server snapshot is the same function as the client snapshot. Tesseract isolates each SSR
 * request, so module state is per-request and safe to read on the server, and using one snapshot for
 * both means a hydrating render agrees with the server markup. React requires the third argument
 * during server render, so it cannot simply be omitted.
 */

/**
 * Combined height of every visible banner, replacing `bannerState.getTotalHeight(includeTopNav)`.
 *
 * Pass `includeTopNav` from `BannerContext` exactly as the container-based call sites already do:
 * `const { includeTopNav } = useContext(BannerContext)`.
 */
export function useBannerTotalHeight(includeTopNav: boolean = true): number {
	const { suppressed } = useContext(BannerContext);
	// A number, so `Object.is` compares by value and recomputing per call is safe.
	const getHeight = useCallback(
		() => getBanners().reduce((total, banner) => total + banner.height, 0),
		[],
	);
	const bannerHeight = useSyncExternalStore(subscribeToBanners, getHeight, getHeight);
	// Not reactive, and resolving it reads two flags — so resolve once per mount, as the container
	// does once per instance.
	const topNavHeight = useMemo(() => resolveTopNavHeight(), []);

	if (suppressed) {
		return 0;
	}

	return includeTopNav ? bannerHeight + topNavHeight : bannerHeight;
}

/**
 * Visible banner names joined with "-", replacing `bannerState.getBannerList()`.
 */
export function useBannerList(): string {
	const { suppressed } = useContext(BannerContext);
	const getList = useCallback(
		() =>
			getBanners()
				.map((banner) => banner.name)
				.join('-'),
		[],
	);
	const bannerList = useSyncExternalStore(subscribeToBanners, getList, getList);

	return suppressed ? '' : bannerList;
}

/**
 * Whether any banner other than `name` is currently visible — the check a banner makes to decide
 * whether it is blocked by a higher-priority one.
 */
export function useHasOtherBanners(name: string): boolean {
	const { suppressed } = useContext(BannerContext);
	// Memoised per name: a fresh closure each render would resubscribe on every render.
	const getSnapshot = useCallback(
		() => getBanners().some((banner) => banner.name !== name),
		[name],
	);
	const hasOthers = useSyncExternalStore(subscribeToBanners, getSnapshot, getSnapshot);

	return suppressed ? false : hasOthers;
}

export type BannerActions = {
	show: (name: string, height: number) => void;
	hide: (name: string) => void;
};

/**
 * Write-only access to the banner stack. Deliberately does not subscribe, so a banner that only
 * registers and deregisters itself never re-renders when an unrelated banner changes — today such
 * consumers re-render on every banner change for nothing.
 *
 * Callers must still `show` before becoming visible and `hide` when they are not, or `BannerSpacer`
 * will be wrong.
 */
export function useBannerActions(): BannerActions {
	const { suppressed } = useContext(BannerContext);

	return useMemo(
		() => ({
			show: (name: string, height: number) => {
				if (suppressed) {
					return;
				}
				showBanner(name, height);
			},
			hide: (name: string) => {
				if (suppressed) {
					return;
				}
				hideBanner(name);
			},
		}),
		[suppressed],
	);
}
